import * as THREE from "three";
import { config } from "../config.js";
import { rockGeometry } from "./stages.js";
import { fieldOf } from "../sim/scale.js";
import { floorYOf, markScale } from "./scale-view.js";

// ステージの仕掛けと、ポッドの形ではない敵の見た目(2026-10-30 段階1)。world を見て描くだけ。
// - 機雷(黄玉): 黄色いとげのある玉。近くで止まると速く点滅し、爆発する範囲を赤い輪で見せる
// - 補給衛星: 箱と太陽電池の板(緑と金。撃つと得をする物の色)
// - 流れてくる岩
// - レーザーの柵(赤い棒)と、対空砲(予告の細い線 → 縦の光の柱)
// ポッドの形の敵(緑玉・赤玉・青玉・紫玉)は index.js でポッドとして描く(POD_LOOKS)。
// スケール感(src/sim/scale.js): 敵は e.size、柵・対空砲は出たときの太さ(h.w)で描く。

export const OWN_MESH_TYPES = new Set(["mine", "satellite", "rock"]);

// ポッドとして描く敵の大きさと色(白い塗装に色を掛ける。1 がそのまま)。
// 2026-10-31 オーナー決定: 色の玉の敵。緑玉=pod・赤玉=dasher・青玉=gunner・紫玉=splitter(割れた shard も)。黄玉=mine は下の機雷の形
export const POD_LOOKS = {
  pod: { scale: 1, color: [0.45, 1.5, 0.55] },
  dasher: { scale: 0.85, color: [1.7, 0.4, 0.35] },
  gunner: { scale: 1.35, color: [0.45, 0.8, 1.9] },
  splitter: { scale: 1.25, color: [1.3, 0.5, 1.8] },
  shard: { scale: 0.6, color: [1.3, 0.5, 1.8] },
  escort: { scale: 1.8, color: [0.42, 0.44, 0.5] }, // 大玉の護衛のドローンポッド(ダークシルバー。当たり判定の半径 9m ÷ ポッドの 5m)
};

export function createStageObjects(scene) {
  const T = config.enemies.types;
  const G = config.gimmicks;

  function inst(geo, mat, max) {
    const m = new THREE.InstancedMesh(geo, mat, max);
    m.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    m.frustumCulled = false;
    m.count = 0;
    scene.add(m);
    return m;
  }
  const add = { blending: THREE.AdditiveBlending, transparent: true, depthWrite: false };

  const mines = inst(new THREE.IcosahedronGeometry(T.mine.radius, 0), new THREE.MeshStandardMaterial({ color: 0x8a7a22, emissive: 0xffc020, emissiveIntensity: 0.45, metalness: 0.6, roughness: 0.4, flatShading: true }), 120);
  mines.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(120 * 3).fill(1), 3);
  const mineRings = inst(new THREE.RingGeometry(0.92, 1, 40), new THREE.MeshBasicMaterial({ color: 0xff3030, ...add, opacity: 0.7, side: THREE.DoubleSide }), 120);

  const satBody = inst(new THREE.BoxGeometry(5, 5, 5), new THREE.MeshStandardMaterial({ color: 0xffe08a, emissive: 0xffaa00, emissiveIntensity: 0.6, metalness: 0.5, roughness: 0.3 }), 10);
  const satPanel = inst(new THREE.BoxGeometry(22, 0.5, 6), new THREE.MeshStandardMaterial({ color: 0x3dff8a, emissive: 0x1fcc55, emissiveIntensity: 0.9, metalness: 0.4, roughness: 0.4 }), 10);

  const rockTex = new THREE.TextureLoader().load("assets/textures/asteroid-color.jpg");
  rockTex.colorSpace = THREE.SRGBColorSpace;
  const rocks = inst(rockGeometry(), new THREE.MeshStandardMaterial({ map: rockTex, roughness: 1, metalness: 0.05, emissive: 0xff3010, emissiveIntensity: 0.35 }), 80); // 背景の岩と分けるため、赤く光らせる(危ない物)
  rocks.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(80 * 3).fill(1), 3);

  // 柵: 長さ1の棒を、向きに合わせて伸ばす
  const fences = inst(new THREE.BoxGeometry(1, 1, 1), new THREE.MeshBasicMaterial({ color: 0xff2a2a, ...add }), 12);
  const fenceCores = inst(new THREE.BoxGeometry(1, 1, 1), new THREE.MeshBasicMaterial({ color: 0xffe0e0, ...add }), 12);
  // 対空砲: 縦の柱(高さ1)
  const flaks = inst(new THREE.BoxGeometry(1, 1, 1), new THREE.MeshBasicMaterial({ color: 0xff3a1a, ...add, opacity: 1 }), 12);

  const m4 = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const e3 = new THREE.Euler();
  const v = new THREE.Vector3();
  const sc = new THREE.Vector3();
  const camQ = new THREE.Quaternion();
  const ident = new THREE.Quaternion();

  function put(mesh, i, x, y, z, rot, sx, sy = sx, sz = sx) {
    sc.set(sx, sy, sz);
    m4.compose(v.set(x, y, z), rot, sc);
    mesh.setMatrixAt(i, m4);
  }
  function done(...meshes) {
    for (const m of meshes) {
      m.instanceMatrix.needsUpdate = true;
      if (m.instanceColor) m.instanceColor.needsUpdate = true;
    }
  }

  // hitFlash(id): 被弾して白く光らせる間なら true(index.js の仕組みを使う)
  // grow(e): 大きさに掛ける割合(リプレイの演出カメラで、出る・消えるをポンと見せないため。src/view/replay-camera.js)
  function update(world, now, hitFlash, grow = () => 1) {
    let nm = 0;
    let nr = 0;
    let ns = 0;
    let nk = 0;
    for (const e of world.enemies) {
      if (!OWN_MESH_TYPES.has(e.type)) continue;
      const hit = hitFlash(e);
      const size = e.size * grow(e);
      if (e.type === "mine" && nm < 120) {
        const armed = e.state === 1;
        const blink = armed ? Math.floor(now / 70) % 2 : Math.floor(now / 400 + e.id) % 2;
        const c = hit ? 4 : 1 + blink * (armed ? 3 : 1.2);
        q.setFromEuler(e3.set(e.age * 1.3, e.age * 0.9, 0));
        put(mines, nm, e.x, e.y, e.z, q, size);
        mines.instanceColor.setXYZ(nm, c, c, hit ? 4 : 1); // 黄色のまま明るく点滅
        if (armed) put(mineRings, nr++, e.x, e.y, e.z, camQ, T.mine.blast * size * (1 - Math.max(0, e.cd) / T.mine.fuse * 0.3));
        nm++;
      } else if (e.type === "satellite" && ns < 10) {
        q.setFromEuler(e3.set(0, 0, Math.sin(e.age) * 0.3));
        const s = (hit ? 1.15 : 1) * size;
        put(satBody, ns, e.x, e.y, e.z, q, s);
        put(satPanel, ns, e.x, e.y, e.z, q, s);
        ns++;
      } else if (e.type === "rock" && nk < 80) {
        q.setFromEuler(e3.set(e.age * 0.7 + e.id, e.age * 0.5, e.id));
        put(rocks, nk, e.x, e.y, e.z, q, T.rock.radius * 1.2 * size);
        const c = hit ? 3 : 1;
        rocks.instanceColor.setXYZ(nk, c, c, c);
        nk++;
      }
    }
    mines.count = nm;
    mineRings.count = nr;
    satBody.count = satPanel.count = ns;
    rocks.count = nk;
    done(mines, mineRings, satBody, satPanel, rocks);

    // 柵と対空砲
    let nf = 0;
    let nq = 0;
    const f = fieldOf(world);
    const sc1 = world.scale;
    const fieldW = (f.halfWidth + 20 * sc1) * 2;
    const fieldH = (f.halfHeight + 20 * sc1) * 2;
    const floorY = floorYOf(world);
    const pulse = 1 + Math.sin(now / 60) * 0.25;
    for (const h of world.hazards) {
      if (h.k === "fence" && nf < 12) {
        const w = h.w * 2 * pulse;
        if (h.axis === "x") {
          put(fences, nf, h.pos, 0, h.z, ident, w, fieldH, w);
          put(fenceCores, nf, h.pos, 0, h.z, ident, w * 0.3, fieldH, w * 0.3);
        } else {
          put(fences, nf, 0, h.pos, h.z, ident, fieldW, w, w);
          put(fenceCores, nf, 0, h.pos, h.z, ident, fieldW, w * 0.3, w * 0.3);
        }
        nf++;
      } else if (h.k === "flak" && nq < 12) {
        const firing = h.t >= G.flak.warnSeconds;
        const top = f.halfHeight + 30 * sc1;
        // 予告: 細い線(だんだん速く点滅)。発射: 太い柱
        const k = h.t / G.flak.warnSeconds;
        const on = firing || Math.floor(now / (160 - 110 * k)) % 2 === 0;
        const w = firing ? h.w * 2 * pulse : on ? 0.8 * markScale(world) : 0.001;
        put(flaks, nq++, h.x, (floorY + top) / 2, 0, ident, w, top - floorY, w);
      }
    }
    fences.count = fenceCores.count = nf;
    flaks.count = nq;
    done(fences, fenceCores, flaks);
  }

  return {
    update,
    faceCamera(camera) {
      camQ.copy(camera.quaternion);
    },
  };
}
